"use client";

import { useState, useEffect } from "react";
import { QRCodeCanvas } from "qrcode.react";
import { motion } from "framer-motion";
import { getNbfcPaymentDetails } from "../../config/nbfcPaymentConfig";

const PaymentResult = ({ response, retry }) => {
  const isSuccess = response?.status === "success";

  return (
    <div className="flex flex-col items-center justify-center p-8">
      <div
        className={`w-20 h-20 rounded-full flex items-center justify-center mb-4 ${
          isSuccess ? "bg-green-100" : "bg-red-100"
        }`}
      >
        {isSuccess ? (
          <svg
            className="w-12 h-12 text-green-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M5 13l4 4L19 7"
            />
          </svg>
        ) : (
          <svg
            className="w-12 h-12 text-red-600"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        )}
      </div>
      <h2
        className={`text-2xl font-bold mb-2 ${isSuccess ? "text-green-600" : "text-red-600"}`}
      >
        {isSuccess ? "Payment Successful!" : "Payment Failed"}
      </h2>
      <p className="text-gray-600 mb-6 text-center">
        {isSuccess
          ? "Your payment has been processed successfully."
          : "There was an issue processing your payment. Please try again."}
      </p>
      {response?.txnid && (
        <p className="text-sm text-gray-500 mb-4">
          Transaction ID: {response.txnid}
        </p>
      )}
      <button
        onClick={retry}
        className="bg-[#4FA0FF] hover:bg-[#3A8CE6] text-white font-semibold py-3 px-8 rounded-lg"
      >
        {isSuccess ? "Make Another Payment" : "Try Again"}
      </button>
    </div>
  );
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
    const [dd, mm, yyyy] = dateStr.split("-");
    const isoDateStr = `${yyyy}-${mm}-${dd}`;
    const date = new Date(isoDateStr);
    return isNaN(date) ? null : date;
  }
  const date = new Date(dateStr);
  return isNaN(date) ? null : date;
};

export default function PanVerification({ loading, fetchData, onLoanFetched }) {
  const [pancard, setPancard] = useState("");
  const [upiSource, setUpiSource] = useState("crm");
  const [isScriptLoaded, setIsScriptLoaded] = useState(false);
  const [paymentPending, setPaymentPending] = useState(false);
  const [paymentResponse, setPaymentResponse] = useState(null);
  const [paymentToken, setPaymentToken] = useState(null);
  const [showPartPaymentModal, setShowPartPaymentModal] = useState(false);
  const [partPaymentAmount, setPartPaymentAmount] = useState("");
  const [partPaymentError, setPartPaymentError] = useState("");
  const [showDirectPaymentLoader, setShowDirectPaymentLoader] = useState(false);

  const [userData, setUserData] = useState({});
  const [isFetching, setIsFetching] = useState(false);

  const nbfcDetails = getNbfcPaymentDetails(userData?.nbfc);
  const upiId = nbfcDetails.upiId;
  const companyName = nbfcDetails.companyName;

  const upiLink = nbfcDetails.buildUpiLink(userData?.["total_due_amount"]);

  const handlePAN = (e) => {
    const value = e.target.value.toUpperCase();
    if (
      /^[A-Z]{0,5}$/.test(value.slice(0, 5)) &&
      /^[\d]{0,4}$/.test(value.slice(5, 9)) &&
      /^[A-Z]{0,1}$/.test(value.slice(9))
    ) {
      setPancard(value);
    }
  };

  const formatDate = (dateString) => {
    const date = parseDate(dateString);
    if (!date) return "Invalid Date";

    return date.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  };

  const handleSubmit = async () => {
    if (Object.keys(userData).length > 0) {
      window.location.href = upiLink;
      return;
    }

    setIsFetching(true);

    const fetchRepayment = async (url) => {
      const res = await fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pancard }),
      });
      if (!res.ok) throw new Error("Failed to fetch repayment details");
      return await res.json();
    };

    try {
      let responseData = await fetchRepayment(
        "https://crm.salarymates.com/p/api/repayment",
      );

      if (responseData.status !== 1 || !responseData.data) {
        responseData = await fetchRepayment(
          "https://crm.salarymates.com/p/api/repayment",
        );
        if (responseData.status === 1 && responseData.data) {
          setUpiSource("crm");
        }
      } else {
        setUpiSource("cms");
      }

      if (responseData.status !== 1 || !responseData.data) {
        setUserData({ message: "PAN not found" });
      } else {
        setUserData(responseData.data);
        onLoanFetched?.(responseData.data);
      }

      console.log("response Data=>", responseData);
    } catch (error) {
      console.error("Error fetching repayment details:", error);
      throw error;
    } finally {
      setIsFetching(false);
    }
  };

  useEffect(() => {
    const script = document.createElement("script");
    script.src =
      "https://ebz-static.s3.ap-south-1.amazonaws.com/easecheckout/v2.0.0/easebuzz-checkout-v2.min.js";
    script.async = true;
    script.onload = () => {
      console.log("Easebuzz script loaded");
      setIsScriptLoaded(true);
    };
    document.body.appendChild(script);

    return () => {
      document.body.removeChild(script);
    };
  }, []);

  useEffect(() => {
    if (paymentToken && isScriptLoaded) {
      if (window.EasebuzzCheckout) {
        const easebuzzCheckout = new window.EasebuzzCheckout(
          "NR3PPCA10B",
          "prod"
        );

        const options = {
          access_key: paymentToken,
          onResponse: (response) => {
            console.log("Payment response =>", response);
            setPaymentResponse(response);
            setShowDirectPaymentLoader(false);
          },
          theme: "#4FA0FF",
        };
        easebuzzCheckout.initiatePayment(options);
        setTimeout(() => setShowDirectPaymentLoader(false), 1500);
      }
    }
  }, [paymentToken, isScriptLoaded]);

  const handlePartPayment = () => {
    setPartPaymentError("");
    setShowPartPaymentModal(true);
  };

  const validatePartPaymentAmount = (amount) => {
    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount <= 0) {
      return "Please enter a valid amount";
    }
    if (numAmount < 100) {
      return "Minimum payment amount is ₹100";
    }
    if (numAmount > userData.total_due_amount) {
      return `Amount cannot exceed total due amount of ₹${userData.total_due_amount}`;
    }
    return "";
  };

  const processPartPayment = () => {
    const error = validatePartPaymentAmount(partPaymentAmount);
    if (error) {
      setPartPaymentError(error);
      return;
    }

    setShowPartPaymentModal(false);
    generatePaymentToken(userData.lead_id, parseFloat(partPaymentAmount));
  };

  const generatePaymentToken = async (leadID, amount = null) => {
    setPaymentPending(true);
    const requestBody = { leadID };
    if (amount) {
      requestBody.amount = amount;
    }

    const res = await fetch(
      "https://crm.salarymates.com/api/generate_payment_token",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(requestBody),
      },
    ).finally(() => {
      setPaymentPending(false);
    });

    if (!res.ok) throw new Error("Failed to fetch repayment details");
    const response = await res.json();
    console.log(response);
    if (response.status === 1) {
      setPaymentToken(response?.data?.data || response?.data);
    }
  };

  useEffect(() => {
    // Direct repayment link support: ?leadid=X&amt=Y&payment=1
    // If all three are present, skip PAN lookup and go straight to Easebuzz checkout.
    const urlParams = new URLSearchParams(window.location.search);
    const leadID = urlParams.get("leadid");
    const amount = urlParams.get("amt");
    const payment = urlParams.get("payment");

    if (leadID && amount && payment === "1") {
      setShowDirectPaymentLoader(true);
      const numAmount = parseFloat(amount);
      if (!isNaN(numAmount)) {
        generatePaymentToken(leadID, numAmount).catch((err) => {
          console.error(err);
          setShowDirectPaymentLoader(false);
        });
      } else {
        setShowDirectPaymentLoader(false);
      }
    }
  }, [window.location.search]);

  return (
    // <div
    //   className="flex flex-col px-4 py-4 sm:px-1 sm:py-1"
    //   style={{ background: "#FFFFFF" }}
    // >
    //   {showPartPaymentModal && (
    //     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
    //       <div className="bg-white rounded-lg p-6 w-full max-w-md border border-[#E5E5E5]">
    //         <h2 className="text-xl font-bold mb-4 text-[#1D3557]">
    //           Part Payment
    //         </h2>
    //         <p className="text-[#1D3557] mb-4">
    //           Enter the amount you wish to pay now. Minimum payment is ₹100.
    //         </p>

    //         <div className="mb-4">
    //           <label className="block text-[#1D3557] mb-2">
    //             Payment Amount (₹)
    //           </label>
    //           <input
    //             type="number"
    //             value={partPaymentAmount}
    //             onChange={(e) => setPartPaymentAmount(e.target.value)}
    //             min="100"
    //             max={userData.total_due_amount}
    //             className="w-full p-3 border border-[#E5E5E5] rounded-md text-[#1D3557]"
    //             placeholder="Enter amount"
    //           />
    //           {partPaymentError && (
    //             <p className="text-red-500 text-sm mt-1">{partPaymentError}</p>
    //           )}
    //           <p className="text-sm text-[#1D3557] mt-2">
    //             Total due amount: ₹{userData.total_due_amount}
    //           </p>
    //         </div>

    //         <div className="flex justify-end space-x-3">
    //           <button
    //             onClick={() => setShowPartPaymentModal(false)}
    //             className="px-4 py-2 border border-[#E5E5E5] rounded-md text-[#1D3557] hover:bg-gray-100"
    //           >
    //             Cancel
    //           </button>
    //           <button
    //             onClick={processPartPayment}
    //             className="px-4 py-2 bg-[#FFD700] text-[#1D3557] rounded-md hover:bg-[#FFE55C] font-semibold"
    //           >
    //             Process Payment
    //           </button>
    //         </div>
    //       </div>
    //     </div>
    //   )}
    //   {!paymentResponse ? (
    //     <div
    //       className="flex flex-col md:flex-row mt-4 sm:mt-2 border border-[#E5E5E5] rounded-md"
    //       style={{ background: "#FFFFFF" }}
    //     >
    //       <div
    //         className="w-full md:w-1/2 px-4 py-4 sm:px-2 sm:py-2 border-b md:border-b-0 md:border-r border-[#E5E5E5]"
    //         style={{ background: "#FFFFFF" }}
    //       >
    //         <p className="text-[#1D3557] italic mb-5">
    //           Please verify the accuracy of the below details before doing any
    //           transfer.
    //         </p>
    //         <span className="mb-2 text-[#1D3557]">
    //           Please enter your PAN Details
    //         </span>
    //         <input
    //           type="text"
    //           value={pancard}
    //           onChange={handlePAN}
    //           required
    //           maxLength={10}
    //           className="p-3 border border-[#E5E5E5] rounded-md text-base w-full max-w-md text-[#1D3557]"
    //           style={{ background: "#FFFFFF" }}
    //         />
    //         <button
    //           onClick={handleSubmit}
    //           className="bg-[#FFD700] mt-2 hover:bg-[#FFE55C] text-[#1D3557] border-none rounded-md py-3 px-8 text-base cursor-pointer transition-colors inline-block font-semibold"
    //         >
    //           {isFetching ? (
    //             <div className="inline-block w-5 h-5 border-4 border-[#1D3557] border-t-transparent rounded-full animate-spin"></div>
    //           ) : userData?.total_due_amount ? (
    //             `Pay via UPI ₹${userData.total_due_amount}`
    //           ) : (
    //             "View My Loan"
    //           )}
    //         </button>
    //         {Object.keys(userData).length > 0 && (
    //           <div
    //             className="mt-4 sm:mt-6 p-4 bg-gray-50 border border-[#E5E5E5] rounded-lg shadow-md w-full"
    //             style={{ background: "#FFFFFF" }}
    //           >
    //             {userData.message ? (
    //               <p className="text-center text-red-600 font-semibold">
    //                 {userData.message}
    //               </p>
    //             ) : (
    //               <>
    //                 {userData.loan_no && userData.total_due_amount && (
    //                   <div className="mb-4 text-center">
    //                     <p className="text-lg font-semibold text-[#1D3557]">
    //                       Total Due Amount: ₹{userData.total_due_amount}
    //                     </p>
    //                     {userData.repayment_date && (
    //                       <p
    //                         className={`text-sm font-medium mt-1 ${
    //                           new Date() >= new Date(userData.repayment_date)
    //                             ? "text-red-600"
    //                             : "text-[#1D3557]"
    //                         }`}
    //                       >
    //                         Repayment Due Date:{" "}
    //                         {formatDate(userData.repayment_date)}
    //                       </p>
    //                     )}
    //                   </div>
    //                 )}
    //                 <h3 className="text-xl font-bold text-center mb-4 text-[#4FA0FF]">
    //                   Summary
    //                 </h3>
    //                 <div className="space-y-2 text-sm sm:text-base">
    //                   {userData.loan_no && (
    //                     <p className="text-[#1D3557]">
    //                       <strong>Loan No:</strong> {userData.loan_no}
    //                     </p>
    //                   )}
    //                   {userData.repayment_amount && (
    //                     <p className="text-[#1D3557]">
    //                       <strong>Repayment Amount:</strong> ₹
    //                       {userData.repayment_amount}
    //                     </p>
    //                   )}
    //                   {userData.total_interest_amount && (
    //                     <p className="text-[#1D3557]">
    //                       <strong>Interest:</strong> ₹
    //                       {userData.total_interest_amount}
    //                     </p>
    //                   )}

    //                   {userData.disbursal_date && (
    //                     <p className="text-[#1D3557]">
    //                       <strong>Disbursal Date:</strong>{" "}
    //                       {formatDate(userData.disbursal_date)}
    //                     </p>
    //                   )}
    //                   {userData.repayment_date && (
    //                     <p className="text-[#1D3557]">
    //                       <strong>Repayment Date:</strong>{" "}
    //                       {formatDate(userData.repayment_date)}
    //                     </p>
    //                   )}
    //                   {userData.tenure && (
    //                     <p className="text-[#1D3557]">
    //                       <strong>Tenure (Days):</strong> {userData.tenure}
    //                     </p>
    //                   )}
    //                   {userData.penalty_days && (
    //                     <p className="text-[#1D3557]">
    //                       <strong>Penalty Days</strong> {userData.penalty_days}
    //                     </p>
    //                   )}
    //                   <p className="font-semibold text-[#4FA0FF]">
    //                     <strong>Total Due Amount:</strong> ₹
    //                     {userData.total_due_amount}
    //                   </p>
    //                 </div>
    //               </>
    //             )}
    //           </div>
    //         )}
    //       </div>
    //       <div
    //         className="w-full md:w-1/2 px-4 py-4 sm:px-2 sm:py-2 flex flex-col items-center justify-center"
    //         style={{ background: "#FFFFFF" }}
    //       >
    //         <h3 className="text-xl font-bold mb-2 text-center text-[#1D3557]">
    //           {companyName}
    //         </h3>
    //         <h4 className="text-lg font-semibold mb-4 text-[#1D3557]">
    //           Scan to Pay
    //         </h4>
    //         <div
    //           className="border border-[#E5E5E5] rounded-md p-3 sm:p-2 mb-2"
    //           style={{ background: "#FFFFFF" }}
    //         >
    //           <QRCodeCanvas
    //             value={`upi://pay?pa=${upiId}&pn=POSUser&am=${userData?.["total_due_amount"]}&cu=INR`}
    //             size={200}
    //           />
    //         </div>
    //         <div className="flex flex-col gap-2 mt-4 w-full max-w-xs">
    //           <button
    //             onClick={() =>
    //               (window.location.href = `upi://pay?pa=${upiId}&pn=KMUser&am=${userData?.["total_due_amount"]}&cu=INR`)
    //             }
    //             className="bg-[#FFD700] hover:bg-[#FFE55C] cursor-pointer text-[#1D3557]   py-2 px-4 rounded text-center font-semibold"
    //           >
    //             Pay ₹{userData?.["total_due_amount"] || ""} to Server 1
    //           </button>
    //           {/* <button
    //             onClick={() =>
    //               (window.location.href = `upi://pay?pa=${upiId}&pn=POSUser&am=${userData?.["total_due_amount"]}&cu=INR`)
    //             }
    //             className="bg-[#4FA0FF] hover:bg-[#3A8CE6] cursor-pointer text-white py-2 px-4 rounded text-center font-semibold"
    //           >
    //             Pay ₹{userData?.["total_due_amount"] || ""} to Server 2
    //           </button> */}
    //           {/* {userData.loan_no && userData.total_due_amount && (
    //             <>
    //               <button
    //                 disabled={paymentPending}
    //                 onClick={() => generatePaymentToken(userData.lead_id)}
    //                 className="bg-[#FFD700] hover:bg-[#FFE55C] text-[#1D3557] font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 font-semibold"
    //               >
    //                 <svg
    //                   xmlns="http://www.w3.org/2000/svg"
    //                   className="h-5 w-5"
    //                   viewBox="0 0 20 20"
    //                   fill="currentColor"
    //                 >
    //                   <path
    //                     fillRule="evenodd"
    //                     d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
    //                     clipRule="evenodd"
    //                   />
    //                 </svg>
    //                 {!paymentPending
    //                   ? "Pay Securely via Easebuzz"
    //                   : "Initiating Payment..."}
    //               </button>
    //               <button
    //                 onClick={handlePartPayment}
    //                 className="bg-[#4FA0FF] hover:bg-[#3A8CE6] text-white font-medium py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 font-semibold"
    //               >
    //                 <svg
    //                   xmlns="http://www.w3.org/2000/svg"
    //                   className="h-5 w-5"
    //                   viewBox="0 0 20 20"
    //                   fill="currentColor"
    //                 >
    //                   <path
    //                     fillRule="evenodd"
    //                     d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
    //                     clipRule="evenodd"
    //                   />
    //                 </svg>
    //                 Part Payment
    //               </button>
    //             </>
    //           )} */}
    //         </div>
    //       </div>
    //     </div>
    //   ) : (
    //     <PaymentResult
    //       retry={() => setPaymentResponse(null)}
    //       response={paymentResponse}
    //     />
    //   )}
    //   <div className="mt-8"></div>
    // </div>
    // <div className="flex flex-col px-4 py-4 sm:px-1 sm:py-1 bg-white">
    //   {/* Part Payment Modal */}
    //   {showPartPaymentModal && (
    //     <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
    //       <motion.div
    //         initial={{ opacity: 0, scale: 0.9 }}
    //         animate={{ opacity: 1, scale: 1 }}
    //         exit={{ opacity: 0, scale: 0.9 }}
    //         className="bg-white rounded-xl shadow-2xl p-6 w-full max-w-md border border-gray-100 relative overflow-hidden"
    //       >
    //         {/* Floating Elements */}
    //         <div className="absolute inset-0 pointer-events-none">
    //           <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#105840]/5 rounded-full blur-3xl"></div>
    //           <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#105840]/5 rounded-full blur-3xl"></div>
    //         </div>

    //         <div className="relative z-10">
    //           {/* Green Line Above Heading */}
    //           <div className="flex items-center gap-3 mb-4">
    //             <motion.div
    //               initial={{ width: 0 }}
    //               animate={{ width: "40px" }}
    //               transition={{ duration: 0.8 }}
    //               className="h-1 bg-[#105840] rounded-full"
    //             />
    //             <span className="text-xs font-medium text-[#105840] tracking-wider">
    //               PART PAYMENT
    //             </span>
    //           </div>

    //           <h2 className="text-2xl font-bold mb-4 text-gray-900">
    //             Part Payment
    //           </h2>
    //           <p className="text-gray-600 mb-4">
    //             Enter the amount you wish to pay now. Minimum payment is ₹100.
    //           </p>

    //           <div className="mb-4">
    //             <label className="block text-gray-700 mb-2 font-medium">
    //               Payment Amount (₹)
    //             </label>
    //             <input
    //               type="number"
    //               value={partPaymentAmount}
    //               onChange={(e) => setPartPaymentAmount(e.target.value)}
    //               min="100"
    //               max={userData.total_due_amount}
    //               className="w-full p-3 border border-gray-200 rounded-lg text-gray-900 focus:outline-none focus:border-[#105840] focus:ring-1 focus:ring-[#105840]/20 transition-all"
    //               placeholder="Enter amount"
    //             />
    //             {partPaymentError && (
    //               <p className="text-red-500 text-sm mt-1">
    //                 {partPaymentError}
    //               </p>
    //             )}
    //             <p className="text-sm text-gray-600 mt-2">
    //               Total due amount: ₹{userData.total_due_amount}
    //             </p>
    //           </div>

    //           <div className="flex justify-end space-x-3">
    //             <button
    //               onClick={() => setShowPartPaymentModal(false)}
    //               className="px-4 py-2 border border-gray-200 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
    //             >
    //               Cancel
    //             </button>
    //             <button
    //               onClick={processPartPayment}
    //               className="px-4 py-2 bg-[#105840] text-white rounded-lg hover:bg-[#0A4A30] transition-colors font-semibold"
    //             >
    //               Process Payment
    //             </button>
    //           </div>
    //         </div>
    //       </motion.div>
    //     </div>
    //   )}

    //   {!paymentResponse ? (
    //     <motion.div
    //       initial={{ opacity: 0, y: 20 }}
    //       animate={{ opacity: 1, y: 0 }}
    //       transition={{ duration: 0.6 }}
    //       className="flex flex-col md:flex-row mt-4 sm:mt-2 border border-gray-200 rounded-xl overflow-hidden bg-white shadow-lg"
    //     >
    //       {/* Left Column - Form & Details */}
    //       <div className="w-full md:w-1/2 px-6 py-6 border-b md:border-b-0 md:border-r border-gray-200 bg-white">
    //         {/* Green Line Above Heading */}
    //         <div className="flex items-center gap-3 mb-4">
    //           <motion.div
    //             initial={{ width: 0 }}
    //             animate={{ width: "40px" }}
    //             transition={{ duration: 0.8 }}
    //             className="h-1 bg-[#105840] rounded-full"
    //           />
    //           <span className="text-xs font-medium text-[#105840] tracking-wider">
    //             PAYMENT DETAILS sxjsks
    //           </span>
    //         </div>

    //         <p className="text-gray-600 italic mb-5">
    //           Please verify the accuracy of the below details before doing any
    //           transfer.
    //         </p>

    //         <span className="mb-2 text-gray-700 block font-medium">
    //           Please enter your PAN Details jhbjhbhjbjh
    //         </span>
    //         <input
    //           type="text"
    //           value={pancard}
    //           onChange={handlePAN}
    //           required
    //           maxLength={10}
    //           className="p-3 border border-gray-200 rounded-lg text-base w-full max-w-md text-gray-900 focus:outline-none focus:border-[#105840] focus:ring-1 focus:ring-[#105840]/20 transition-all"
    //           placeholder="Enter PAN"
    //         />

    //         <button
    //           onClick={handleSubmit}
    //           className="bg-[#105840] mt-4 hover:bg-[#0A4A30] text-white border-none rounded-lg py-3 px-8 text-base cursor-pointer transition-all font-semibold inline-flex items-center justify-center shadow-md hover:shadow-lg"
    //         >
    //           {isFetching ? (
    //             <div className="inline-block w-5 h-5 border-4 border-white border-t-transparent rounded-full animate-spin"></div>
    //           ) : userData?.total_due_amount ? (
    //             `Pay via UPI ₹${userData.total_due_amount}`
    //           ) : (
    //             "View My Loan"
    //           )}
    //         </button>

    //         {/* User Data Display */}
    //         {Object.keys(userData).length > 0 && (
    //           <motion.div
    //             initial={{ opacity: 0, y: 20 }}
    //             animate={{ opacity: 1, y: 0 }}
    //             transition={{ delay: 0.2 }}
    //             className="mt-6 p-5 bg-gray-50 border border-gray-200 rounded-xl w-full"
    //           >
    //             {userData.message ? (
    //               <p className="text-center text-red-600 font-semibold">
    //                 {userData.message}
    //               </p>
    //             ) : (
    //               <>
    //                 {userData.loan_no && userData.total_due_amount && (
    //                   <div className="mb-4 text-center pb-4 border-b border-gray-200">
    //                     <p className="text-xl font-bold text-[#105840]">
    //                       Total Due Amount: ₹{userData.total_due_amount}
    //                     </p>
    //                     {userData.repayment_date && (
    //                       <p
    //                         className={`text-sm font-medium mt-2 ${
    //                           new Date() >= new Date(userData.repayment_date)
    //                             ? "text-red-600"
    //                             : "text-gray-600"
    //                         }`}
    //                       >
    //                         Repayment Due Date:{" "}
    //                         {formatDate(userData.repayment_date)}
    //                       </p>
    //                     )}
    //                   </div>
    //                 )}

    //                 <h3 className="text-lg font-bold text-center mb-4 text-[#105840]">
    //                   Summary
    //                 </h3>

    //                 <div className="space-y-2 text-sm">
    //                   {userData.loan_no && (
    //                     <div className="flex justify-between items-center py-1 border-b border-gray-100">
    //                       <span className="text-gray-600">Loan No:</span>
    //                       <span className="font-medium text-gray-900">
    //                         {userData.loan_no}
    //                       </span>
    //                     </div>
    //                   )}
    //                   {userData.repayment_amount && (
    //                     <div className="flex justify-between items-center py-1 border-b border-gray-100">
    //                       <span className="text-gray-600">
    //                         Repayment Amount:
    //                       </span>
    //                       <span className="font-medium text-gray-900">
    //                         ₹{userData.repayment_amount}
    //                       </span>
    //                     </div>
    //                   )}
    //                   {userData.total_interest_amount && (
    //                     <div className="flex justify-between items-center py-1 border-b border-gray-100">
    //                       <span className="text-gray-600">Interest:</span>
    //                       <span className="font-medium text-gray-900">
    //                         ₹{userData.total_interest_amount}
    //                       </span>
    //                     </div>
    //                   )}
    //                   {userData.disbursal_date && (
    //                     <div className="flex justify-between items-center py-1 border-b border-gray-100">
    //                       <span className="text-gray-600">Disbursal Date:</span>
    //                       <span className="font-medium text-gray-900">
    //                         {formatDate(userData.disbursal_date)}
    //                       </span>
    //                     </div>
    //                   )}
    //                   {userData.repayment_date && (
    //                     <div className="flex justify-between items-center py-1 border-b border-gray-100">
    //                       <span className="text-gray-600">Repayment Date:</span>
    //                       <span className="font-medium text-gray-900">
    //                         {formatDate(userData.repayment_date)}
    //                       </span>
    //                     </div>
    //                   )}
    //                   {userData.tenure && (
    //                     <div className="flex justify-between items-center py-1 border-b border-gray-100">
    //                       <span className="text-gray-600">Tenure (Days):</span>
    //                       <span className="font-medium text-gray-900">
    //                         {userData.tenure}
    //                       </span>
    //                     </div>
    //                   )}
    //                   {userData.penalty_days && (
    //                     <div className="flex justify-between items-center py-1 border-b border-gray-100">
    //                       <span className="text-gray-600">Penalty Days:</span>
    //                       <span className="font-medium text-gray-900">
    //                         {userData.penalty_days}
    //                       </span>
    //                     </div>
    //                   )}
    //                   <div className="flex justify-between items-center py-2 mt-2 bg-[#105840]/5 rounded-lg px-2">
    //                     <span className="font-semibold text-[#105840]">
    //                       Total Due Amount:
    //                     </span>
    //                     <span className="font-bold text-[#105840] text-lg">
    //                       ₹{userData.total_due_amount}
    //                     </span>
    //                   </div>
    //                 </div>
    //               </>
    //             )}
    //           </motion.div>
    //         )}
    //       </div>

    //       {/* Right Column - QR Code & Payment Options */}
    //       <div className="w-full md:w-1/2 px-6 py-6 flex flex-col items-center justify-center bg-white">
    //         {/* Green Line Above Heading */}
    //         <div className="flex items-center gap-3 mb-4 self-start">
    //           <motion.div
    //             initial={{ width: 0 }}
    //             animate={{ width: "40px" }}
    //             transition={{ duration: 0.8 }}
    //             className="h-1 bg-[#105840] rounded-full"
    //           />
    //           <span className="text-xs font-medium text-[#105840] tracking-wider">
    //             SCAN & PAY
    //           </span>
    //         </div>

    //         <h3 className="text-xl font-bold mb-2 text-center text-gray-900">
    //           {companyName}
    //         </h3>
    //         <h4 className="text-lg font-medium mb-4 text-gray-700">
    //           Scan to Pay
    //         </h4>

    //         <div className="border border-gray-200 rounded-xl p-4 bg-white shadow-md">
    //           <QRCodeCanvas
    //             value={`upi://pay?pa=${upiId}&pn=POSUser&am=${userData?.["total_due_amount"]}&cu=INR`}
    //             size={200}
    //           />
    //         </div>

    //         <div className="flex flex-col gap-3 mt-6 w-full max-w-xs">
    //           <button
    //             onClick={() =>
    //               (window.location.href = `upi://pay?pa=${upiId}&pn=KMUser&am=${userData?.["total_due_amount"]}&cu=INR`)
    //             }
    //             className="bg-[#105840] hover:bg-[#0A4A30] text-white py-3 px-4 rounded-lg text-center font-semibold transition-all shadow-md hover:shadow-lg"
    //           >
    //             Pay ₹{userData?.["total_due_amount"] || ""} to Server 2
    //           </button>
    //         </div>
    //       </div>
    //     </motion.div>
    //   ) : (
    //     <PaymentResult
    //       retry={() => setPaymentResponse(null)}
    //       response={paymentResponse}
    //     />
    //   )}

    //   <div className="mt-8"></div>
    // </div>
    <div className="flex flex-col px-4 py-4 sm:px-1 sm:py-1 ">
      {showDirectPaymentLoader && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
          <div className="bg-[#1A2F3A] border border-[#2D5F7A]/30 p-8 rounded-xl flex flex-col items-center shadow-2xl">
            <div className="w-12 h-12 border-4 border-[#7BC4E0] border-t-transparent rounded-full animate-spin"></div>
            <p className="mt-4 text-white font-semibold text-lg">
              Initiating Secure Payment...
            </p>
            <p className="text-[#8FAFC7] text-sm mt-2">
              Please do not refresh or close the page
            </p>
          </div>
        </div>
      )}
      {/* Part Payment Modal */}
      {showPartPaymentModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-[#1A2F3A] rounded-xl shadow-2xl p-6 w-full max-w-md border border-[#2D5F7A]/30 relative overflow-hidden"
          >
            {/* Floating Elements */}
            <div className="absolute inset-0 pointer-events-none">
              <div className="absolute -top-10 -right-10 w-32 h-32 bg-[#2D5F7A]/20 rounded-full blur-3xl"></div>
              <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-[#2D5F7A]/20 rounded-full blur-3xl"></div>
            </div>

            <div className="relative z-10">
              {/* Blue Line Above Heading */}
              <div className="flex items-center gap-3 mb-4">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: "40px" }}
                  transition={{ duration: 0.8 }}
                  className="h-1 bg-[#7BC4E0] rounded-full"
                />
                <span className="text-xs font-medium text-[#7BC4E0] tracking-wider">
                  PART PAYMENT
                </span>
              </div>

              <h2 className="text-2xl font-bold mb-4 text-white">
                Part Payment
              </h2>
              <p className="text-[#CCE6F5] mb-4">
                Enter the amount you wish to pay now. Minimum payment is ₹100.
              </p>

              <div className="mb-4">
                <label className="block text-[#CCE6F5] mb-2 font-medium">
                  Payment Amount (₹)
                </label>
                <input
                  type="number"
                  value={partPaymentAmount}
                  onChange={(e) => setPartPaymentAmount(e.target.value)}
                  min="100"
                  max={userData.total_due_amount}
                  className="w-full p-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-white focus:outline-none focus:border-[#7BC4E0] focus:ring-1 focus:ring-[#7BC4E0]/20 transition-all"
                  placeholder="Enter amount"
                />
                {partPaymentError && (
                  <p className="text-red-500 text-sm mt-1">
                    {partPaymentError}
                  </p>
                )}
                <p className="text-sm text-[#8FAFC7] mt-2">
                  Total due amount: ₹{userData.total_due_amount}
                </p>
              </div>

              <div className="flex justify-end space-x-3">
                <button
                  onClick={() => setShowPartPaymentModal(false)}
                  className="px-4 py-2 border border-[#2D5F7A]/30 rounded-lg text-[#CCE6F5] hover:bg-[#1E3B4A]/30 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={processPartPayment}
                  className="px-4 py-2 bg-[#2D5F7A] text-white rounded-lg hover:bg-[#1E3B4A] transition-colors font-semibold"
                >
                  Process Payment
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      )}

      {!paymentResponse ? (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex flex-col md:flex-row mt-4 sm:mt-2 border border-[#2D5F7A]/30  overflow-hidden bg-[#1A2F3A] shadow-lg"
        >
          {/* Left Column - Form & Details */}
          <div className="w-full md:w-1/2 px-6 py-6 border-b md:border-b-0 md:border-r border-[#2D5F7A]/30 bg-[#1A2F3A]">
            {/* Blue Line Above Heading */}
            <div className="flex items-center gap-3 mb-4">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "40px" }}
                transition={{ duration: 0.8 }}
                className="h-1 bg-[#7BC4E0] rounded-full"
              />
              <span className="text-xs font-medium text-[#7BC4E0] tracking-wider">
                PAYMENT DETAILS
              </span>
            </div>

            <p className="text-[#CCE6F5] italic mb-5">
              Please verify the accuracy of the below details before doing any
              transfer.
            </p>

            <span className="mb-2 text-[#CCE6F5] block font-medium">
              Please enter your PAN Details
            </span>
            <input
              type="text"
              value={pancard}
              onChange={handlePAN}
              required
              maxLength={10}
              className="p-3 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-lg text-base w-full max-w-md text-white focus:outline-none focus:border-[#7BC4E0] focus:ring-1 focus:ring-[#7BC4E0]/20 transition-all"
              placeholder="Enter PAN"
            />

            <button
              onClick={handleSubmit}
              className="bg-[#2D5F7A] mt-4  text-white border-none rounded-lg py-3 px-8 text-base cursor-pointer transition-all font-semibold inline-flex items-center justify-center shadow-md hover:shadow-lg"
            >
              {isFetching ? (
                <div className="inline-block w-5 h-5 border-4 border-white  text-white border-t-transparent rounded-full animate-spin"></div>
              ) : userData?.total_due_amount ? (
                `Pay via UPI ₹${userData.total_due_amount}`
              ) : (
                "View My Funds"
              )}
            </button>

            {/* User Data Display */}
            {Object.keys(userData).length > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
                className="mt-6 p-5 bg-[#0F2128] border border-[#2D5F7A]/30 rounded-xl w-full"
              >
                {userData.message ? (
                  <p className="text-center text-red-400 font-semibold">
                    {userData.message}
                  </p>
                ) : (
                  <>
                    {userData.loan_no && userData.total_due_amount && (
                      <div className="mb-4 text-center pb-4 border-b border-[#2D5F7A]/30">
                        <p className="text-xl font-bold text-[#7BC4E0]">
                          Total Due Amount: ₹{userData.total_due_amount}
                        </p>
                        {userData.repayment_date && (
                          <p
                            className={`text-sm font-medium mt-2 ${
                              new Date() >= new Date(userData.repayment_date)
                                ? "text-red-400"
                                : "text-[#8FAFC7]"
                            }`}
                          >
                            Repayment Due Date:{" "}
                            {formatDate(userData.repayment_date)}
                          </p>
                        )}
                      </div>
                    )}

                    <h3 className="text-lg font-bold text-center mb-4 text-[#7BC4E0]">
                      Summary
                    </h3>

                    <div className="space-y-2 text-sm">
                      {userData.loan_no && (
                        <div className="flex justify-between items-center py-1 border-b border-[#2D5F7A]/20">
                          <span className="text-[#8FAFC7]">Loan No:</span>
                          <span className="font-medium text-white">
                            {userData.loan_no}
                          </span>
                        </div>
                      )}
                      {userData.repayment_amount && (
                        <div className="flex justify-between items-center py-1 border-b border-[#2D5F7A]/20">
                          <span className="text-[#8FAFC7]">
                            Repayment Amount:
                          </span>
                          <span className="font-medium text-white">
                            ₹{userData.repayment_amount}
                          </span>
                        </div>
                      )}
                      {userData.total_interest_amount && (
                        <div className="flex justify-between items-center py-1 border-b border-[#2D5F7A]/20">
                          <span className="text-[#8FAFC7]">Interest:</span>
                          <span className="font-medium text-white">
                            ₹{userData.total_interest_amount}
                          </span>
                        </div>
                      )}
                      {userData.disbursal_date && (
                        <div className="flex justify-between items-center py-1 border-b border-[#2D5F7A]/20">
                          <span className="text-[#8FAFC7]">Disbursal Date:</span>
                          <span className="font-medium text-white">
                            {formatDate(userData.disbursal_date)}
                          </span>
                        </div>
                      )}
                      {userData.repayment_date && (
                        <div className="flex justify-between items-center py-1 border-b border-[#2D5F7A]/20">
                          <span className="text-[#8FAFC7]">Repayment Date:</span>
                          <span className="font-medium text-white">
                            {formatDate(userData.repayment_date)}
                          </span>
                        </div>
                      )}
                      {userData.tenure && (
                        <div className="flex justify-between items-center py-1 border-b border-[#2D5F7A]/20">
                          <span className="text-[#8FAFC7]">Tenure (Days):</span>
                          <span className="font-medium text-white">
                            {userData.tenure}
                          </span>
                        </div>
                      )}
                      {Boolean(userData.penalty_days) && (
                        <div className="flex justify-between items-center py-1 border-b border-[#2D5F7A]/20">
                          <span className="text-[#8FAFC7]">Penalty Days:</span>
                          <span className="font-medium text-white">
                            {userData.penalty_days}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center py-2 mt-2 bg-[#2D5F7A]/20 rounded-lg px-2">
                        <span className="font-semibold text-[#7BC4E0]">
                          Total Due Amount:
                        </span>
                        <span className="font-bold text-[#7BC4E0] text-lg">
                          ₹{userData.total_due_amount}
                        </span>
                      </div>
                    </div>
                  </>
                )}
              </motion.div>
            )}
          </div>

          {/* Right Column - QR Code & Payment Options */}
          <div className="w-full md:w-1/2 px-6 py-6 flex flex-col items-center justify-center bg-[#1A2F3A]">
            {/* Blue Line Above Heading */}
            <div className="flex items-center gap-3 mb-4 self-start">
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: "40px" }}
                transition={{ duration: 0.8 }}
                className="h-1 bg-[#7BC4E0] rounded-full"
              />
              <span className="text-xs font-medium text-[#7BC4E0] tracking-wider">
                SCAN & PAY
              </span>
            </div>

            {Object.keys(userData).length > 0 && !userData.message ? (
              <>
                <h3 className="text-xl font-bold mb-2 text-center text-white">
                  {companyName}
                </h3>
                <h4 className="text-lg font-medium mb-4 text-[#CCE6F5]">
                  Scan to Pay
                </h4>

                <div className="border border-[#2D5F7A]/30 rounded-xl p-4 bg-[#0F2128] shadow-md">
                  <QRCodeCanvas
                    value={upiLink}
                    size={200}
                    fgColor="#FFFFFF"
                    bgColor="#0F2128"
                  />
                </div>

                <div className="flex flex-col gap-3 mt-6 w-full max-w-xs">
                  <button
                    onClick={() => (window.location.href = upiLink)}
                    className="bg-[#2D5F7A] hover:bg-[#1E3B4A] text-white py-3 px-4 rounded-lg text-center font-semibold transition-all shadow-md hover:shadow-lg"
                  >
                    Pay ₹{userData?.["total_due_amount"] || ""} to Server 2
                  </button>
                  {userData.loan_no && userData.total_due_amount && (
                    <>
                      <button
                        disabled={paymentPending}
                        onClick={() => generatePaymentToken(userData.lead_id)}
                        className="bg-[#7BC4E0] hover:bg-[#6AB3CF] text-[#0F2128] font-semibold py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        {!paymentPending
                          ? "Pay Securely via Easebuzz"
                          : "Initiating Payment..."}
                      </button>
                      <button
                        onClick={handlePartPayment}
                        className="bg-[#2D5F7A] hover:bg-[#1E3B4A] text-white font-semibold py-3 px-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-5 w-5"
                          viewBox="0 0 20 20"
                          fill="currentColor"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                            clipRule="evenodd"
                          />
                        </svg>
                        Part Payment
                      </button>
                    </>
                  )}
                </div>
              </>
            ) : (
              <p className="text-[#8FAFC7] text-center">
                Enter your PAN and fetch loan details to view payment QR &
                bank info.
              </p>
            )}
          </div>
        </motion.div>
      ) : (
        <PaymentResult
          retry={() => setPaymentResponse(null)}
          response={paymentResponse}
        />
      )}

      <div className="mt-8"></div>
    </div>
  );
}
